import { MouseEventHandler, ReactNode } from 'react';

import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
} from '@mui/material';

import FontAwesomeIcon from '../FontAwesomeIcon';
import { StlButton } from '../StlButton/StlButton';
import { classNames } from '../utils';

import styles from './StlPopup.module.css';

interface StlPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onApply?: () => void;
  onBack?: () => void;
  title?: ReactNode;
  btnTxt?: string;
  children?: ReactNode;
  isAlert?: boolean;
  color?: 'primary' | 'error' | 'errorStl';
  disabled?: boolean;
  cancel?: boolean;
  showCloseBtn?: boolean;
  apply?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
  closeOnApply?: boolean;
}

export function StlPopup({
  isOpen,
  onClose,
  onApply,
  onCancel,
  onBack,
  title,
  children,
  isAlert = false,
  btnTxt = 'Apply',
  color = 'primary',
  disabled = false,
  cancel = true,
  apply = true,
  showCloseBtn = true,
  sx = {},
  className,
  closeOnApply = true,
}: StlPopupProps) {
  const handleClose: MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    if (onCancel) onCancel();
    onClose();
  };

  const handleApply: MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    if (onApply) onApply();
    if (closeOnApply) onClose();
  };

  const handleBack: MouseEventHandler<HTMLElement> = (e) => {
    e.stopPropagation();
    onBack?.();
  };

  const paperClassName = classNames(
    styles.dialog_paper,
    isAlert && styles.alert,
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className={classNames(styles.custom_dialog, className)}
      PaperProps={{
        onMouseDown: (e) => e.stopPropagation(),
        onClick: (e) => e.stopPropagation(),
        className: paperClassName,
        sx,
      }}
    >
      <Box display="flex" className={styles.dialog_title_wrapper}>
        <DialogTitle className={styles.dialog_title}>{title}</DialogTitle>
        {showCloseBtn && (
          <IconButton
            onClick={handleClose}
            size="small"
            className={styles.closeBtn}
          >
            <FontAwesomeIcon name="xmark" type="sharp-solid" size="0.875rem" />
          </IconButton>
        )}
      </Box>
      {children && (
        <DialogContent className={styles.dialog_content}>
          {children}
        </DialogContent>
      )}
      {(cancel || apply) && (
        <DialogActions className={styles.dialog_footer}>
          {cancel && (
            <StlButton
              value="Cancel"
              handleClick={handleClose}
              width={96}
              color={color}
              variant="outlined"
            />
          )}
          {onBack && (
            <StlButton
              value="Back"
              handleClick={handleBack}
              width={96}
              color={color}
              variant="outlined"
            />
          )}
          {apply && (
            <StlButton
              value={btnTxt}
              isActive={!disabled}
              handleClick={handleApply}
              width={96}
              color={color}
            />
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
